'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const COMPLETED_VALUE = 100;
function getInitialState(props) {
  // When value is undefined, default to 0 and start optimistically loading
  const {
    value,
    initialValue
  } = props;
  let valueToUse;
  if (value === undefined) {
    valueToUse = initialValue;
  } else if (value === null) {
    valueToUse = null;
  } else {
    valueToUse = Math.max(initialValue, value);
  }
  return {
    running: false,
    value: valueToUse
  };
}
export default class UIAbstractProgress extends Component {
  constructor(props) {
    super(props);

    // Always keep track of setTimeout id so if the component is unmounted
    // we can cancel any previous timer.
    this.timerId = null;
    this.state = getInitialState(props);
  }
  componentDidMount() {
    const {
      autoStart
    } = this.props;
    if (autoStart && !window.VISUAL_REGRESSION) {
      this.start();
    }
  }
  componentDidUpdate(prevProps) {
    if (!prevProps.autoStart && this.props.autoStart) {
      this.start();
      return;
    }
    if (prevProps.value !== this.props.value) {
      this.incrementProgress(this.props.value);
    }
  }
  componentWillUnmount() {
    const {
      value
    } = this.state;
    this.cancelNextUpdate();
    if (value === null || value >= COMPLETED_VALUE) {
      this.setState({
        value: null
      });
    }
  }
  schedule(fn, increment = this.props.incrementInterval) {
    this.cancelNextUpdate();
    this.timerId = setTimeout(fn, increment);
  }
  scheduleNextUpdate() {
    const {
      maximum
    } = this.props;
    const {
      value
    } = this.state;
    if (value <= maximum) {
      this.schedule(() => {
        this.incrementProgress();
      });
    }
  }
  cancelNextUpdate() {
    clearTimeout(this.timerId);
  }
  start() {
    this.setState({
      running: true
    });
    this.scheduleNextUpdate();
  }
  incrementProgress(nextValue) {
    const {
      initialValue,
      maximum,
      incrementFactor
    } = this.props;
    const {
      value: currentValue
    } = this.state;
    if (this.isComplete(currentValue, nextValue)) {
      this.completeProgress();
      return;
    }
    if (nextValue != null) {
      this.setState({
        value: Math.max(nextValue, initialValue)
      });
      this.scheduleNextUpdate();
    } else if (currentValue != null) {
      // Increment remaining progress by incrementFactor
      // The progress bar won't reach 100% until completeProgress is called
      this.setState({
        value: currentValue + (maximum - currentValue) * incrementFactor
      });
      this.scheduleNextUpdate();
    }
  }
  isComplete(currentValue, nextValue) {
    // if value prop indicates we're done, or we've been spinning
    // our wheels up against the limit, finish off the animation
    return nextValue >= COMPLETED_VALUE || currentValue > COMPLETED_VALUE - 0.5 && nextValue == null;
  }
  completeProgress() {
    const {
      completionLingerTime
    } = this.props;
    this.schedule(() => {
      // Move progress bar to 100%, then allow time for transition effects
      // before finally re-rendering the function with the done === true
      this.setState({
        value: COMPLETED_VALUE
      });
      this.schedule(() => this.setState({
        running: false
      }), completionLingerTime);
    });
  }
  render() {
    const {
      Component: ProgressComponent,
      render
    } = this.props;
    const {
      running,
      value
    } = this.state;
    const done = !running && value >= COMPLETED_VALUE;
    const passedProps = {
      easing: 'linear',
      value,
      done
    };
    if (render) {
      return render(passedProps);
    }
    return /*#__PURE__*/_jsx(ProgressComponent, Object.assign({}, passedProps));
  }
}
UIAbstractProgress.displayName = 'UIAbstractProgress';
UIAbstractProgress.defaultProps = {
  autoStart: true,
  completionLingerTime: 500,
  incrementFactor: 0.25,
  incrementInterval: 150,
  initialValue: 0,
  maximum: COMPLETED_VALUE
};
UIAbstractProgress.propTypes = {
  // start on mount, or no?
  autoStart: PropTypes.bool,
  // Component to render
  Component: PropTypes.elementType,
  // Render prop
  render: PropTypes.func,
  // how long should component "linger" at 100% before
  // `done` is passed? This is helpful for CSS effects
  completionLingerTime: PropTypes.number,
  // Percentage to optimistically creep the bar on each increment
  incrementFactor: PropTypes.number,
  // Time interval to make optimistic creeps
  incrementInterval: PropTypes.number,
  // Time to start the progress bar at to be optimistic
  initialValue: PropTypes.number,
  // maximum percentage of progress via optimism alone
  maximum: PropTypes.number,
  // this is the "real" value of the operation, provided by app
  value: PropTypes.number
};